import { ref, readonly } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { useCookies } from '@/composables/useCookies';
import { isValidNumber } from '@/utils/isValidNumber';
import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
import { waitForChartComponentToBeMounted } from '@/utils/waitForChartComponentToBeMounted';

const NOTIFICATION_PAGE_LOCATION_SUPPORT = 'support';
const NOTIFICATION_PAGE_LOCATION_WORKBENCH = 'workbench';
let hasInitialized = false;
const notifications = ref([]);

async function fetchNotifications() {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;

  const urlSearchParams = new URLSearchParams({
    cmd: 'get-current-notifications-for-user',
    cc: chartCodeCopy,
    location: NOTIFICATION_PAGE_LOCATION_WORKBENCH,
    randomNumber: Date.now().toString()
  });

  let responseJson;

  try {
    const response = await window.fetch(`/json/api?${urlSearchParams.toString()}`, {
      method: 'GET',
      credentials: 'include'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info(`fetch notifications responseJson for chartCode: ${chartCodeCopy}.`, responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch notifications for chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to fetch notifications for chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!responseJson) {
    throw new Error(`Failed to fetch notifications for chartCode: ${chartCodeCopy} - responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
  }

  if (typeof responseJson !== 'object') {
    throw new Error(`Failed to fetch notifications for chartCode: ${chartCodeCopy} - responseJson is truthy but is not an object: ${typeof responseJson}.`);
  }

  if (!responseJson.notifications) {
    throw new Error(`Failed to fetch notifications for chartCode: ${chartCodeCopy} - responseJson.notifications is falsy: ${typeof responseJson.notifications} ${responseJson.notifications}.`);
  }

  if (!Array.isArray(responseJson.notifications)) {
    throw new Error(`Failed to fetch notifications - responseJson.notifications is truthy but is not an array: ${typeof responseJson.notifications}.`);
  }

  return responseJson.notifications;
}

function parseNotifications(rawNotifications) {
  const { chartCode } = useAuthentication();
  const { getCookie } = useCookies();

  if (!rawNotifications) {
    throw new Error(`Unable to parse notifications - rawNotifications is falsy: ${typeof rawNotifications} ${rawNotifications}.`);
  }

  if (!Array.isArray(rawNotifications)) {
    throw new Error(`Unable to parse notifications - rawNotifications is truthy but is not an array: ${typeof rawNotifications}.`);
  }

  const parsedNotifications = [];
  const nowTime = Date.now();

  for (let i = 0; i < rawNotifications.length; i++) {
    const rawNotification = rawNotifications[i];

    if (!rawNotification) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification is falsy: ${typeof rawNotification} ${rawNotification}.`);
      continue;
    }

    if (typeof rawNotification !== 'object') {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification is truthy but is not an object: ${typeof rawNotification} ${rawNotification}.`);
      continue;
    }

    if (typeof rawNotification.locations !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.locations is not a string: ${typeof rawNotification.locations}.`);
      continue;
    }

    if (rawNotification.locations.includes(NOTIFICATION_PAGE_LOCATION_SUPPORT)) {
      continue;
    }

    if (!isValidNumber(rawNotification.startDate, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true })) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.startDate is not a valid integer: ${typeof rawNotification.startDate}.`);
      continue;
    }

    if (!isValidNumber(rawNotification.endDate, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true })) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.endDate is not a valid integer: ${typeof rawNotification.endDate}.`);
      continue;
    }

    if (
      nowTime < Number(rawNotification.startDate) * 1000 ||
      nowTime > Number(rawNotification.endDate) * 1000
    ) {
      continue;
    }

    if (
      !['string', 'number'].includes(typeof rawNotification.ID) ||
      !rawNotification.ID.toString().trim()
    ) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.ID is invalid: ${typeof rawNotification.ID} ${rawNotification.ID}.`);
      continue;
    }

    if (parsedNotifications.find(n => n.id === rawNotification.ID)) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - a notification with the same id: ${rawNotification.ID} already exists.`);
      continue;
    }

    const notificationCookieValue = getCookie(getNotificationCookieKey(rawNotification.ID));

    if (notificationCookieValue) {
      continue;
    }

    if (!isValidNumber(rawNotification.severity)) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.severity is not a valid number: ${typeof rawNotification.severity}.`);
      continue;
    }

    if (![0, 1, 2, 3, 4, 5].includes(Number(rawNotification.severity))) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.severity is a number but is not a valid integer 0-5: ${rawNotification.severity}.`);
      continue;
    }

    if (typeof rawNotification.message !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - rawNotification.message is not a string: ${typeof rawNotification.message}.`);
      continue;
    }

    if (!rawNotification.message.trim()) {
      window.sc3WorkbenchLogger.error(`Unable to parse notification at index ${i} - trimmed rawNotification.message is an empty string.`);
      continue;
    }

    parsedNotifications.push({
      id: rawNotification.ID,
      severity: Number(rawNotification.severity),
      isDismissible: rawNotification.target === chartCode.value,
      message: rawNotification.message.trim(),
      endTime: Number(rawNotification.endDate) * 1000
    });
  }

  return parsedNotifications;
}

function getNotificationCookieKey(notificationId) {
  return `notification-clicked-${notificationId}`;
}

async function dismissNotification({ notificationId }) {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;

  const urlSearchParams = new URLSearchParams({
    cmd: 'dismissnotification',
    cc: chartCodeCopy,
    id: notificationId,
    randomNumber: Date.now().toString()
  });

  let responseJson;

  try {
    const response = await fetch(`/j-notify/notify?${urlSearchParams.toString()}`, {
      method: 'GET'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info(`dismiss notification responseJson for chartCode: ${chartCodeCopy}.`, responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!responseJson) {
    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy} - responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
  }

  if (typeof responseJson !== 'object') {
    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy} - responseJson is truthy but is not an object: ${typeof responseJson}.`);
  }

  if (typeof responseJson.result !== 'string') {
    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy} - responseJson.result is not a string: ${typeof responseJson.result}.`);
  }

  if (!responseJson.result.trim()) {
    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy} - trimmed responseJson.result is an empty string.`);
  }

  if (responseJson.result.trim().toLowerCase() !== 'success') {
    throw new Error(`Failed to dismiss notification with id: ${notificationId} for chartCode: ${chartCodeCopy} - responseJson.result is a non-empty string but is not equal to "success": ${responseJson.result}.`);
  }
}

async function removeNotification({ notification }) {
  const { setCookie } = useCookies();
  const notificationId = notification.id;
  notifications.value = notifications.value.filter(n => n.id !== notificationId);

  if (!notification.isDismissible) {
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;

    setCookie(getNotificationCookieKey(notificationId), '1', new Date(notification.endTime + thirtyDays));
    return;
  }

  try {
    await dismissNotification({ notificationId });
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Error occurred while dismissing notification with id: ${notificationId}.`, e);
  }
}

async function init() {
  const { isLoggedIn } = useAuthentication();

  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  await waitForChartComponentToBeMounted();

  try {
    const isLoggedInCopy = isLoggedIn.value;
    const rawNotifications = await fetchNotifications();

    if (isLoggedInCopy !== isLoggedIn.value) {
      throw new Error(`Failed to retrieve notifications - isLoggedIn.value changed from ${isLoggedInCopy} to ${isLoggedIn.value} while notifications were being fetched.`);
    }

    const parsedNotifications = parseNotifications(rawNotifications);

    parsedNotifications.sort(sortObjectsClosure({
      propertyName: 'severity',
      order: 'descending'
    }));

    notifications.value = parsedNotifications;
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to retrieve notifications.`, e);
  }
}

export function useNotifications() {
  const { addMemberAuthenticationCallback } = useAuthentication();

  addMemberAuthenticationCallback(init);

  return {
    notifications: readonly(notifications),
    removeNotification
  };
}
